import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Navbar from "../nav_bar/Navbar";
import { useAuth } from "../../context/AuthContext";
import { fetchEnrollmentDetail, fetchEnrollmentAssessments, fetchLessonAssessmentClient } from "./clientCourseService";
import { getClientBatchCalendar, submitUnavailability, getMyUnavailability } from "../../services/clientCalendarService";
import "./courses.css";
import "../../../admin_module/calendar/calendar.css";

const CRITERIA_LABELS = {
  scoreFluency:     "Fluency",
  scoreClarity:     "Clarity",
  scoreVocabulary:  "Vocabulary",
  scoreGrammar:     "Grammar",
  scoreInteraction: "Interaction",
};

const MATERIAL_ICON = { GRAMMAR: "📖", READING: "📄", GD: "🗣️", CONTENT_BLOCK: "📝" };
const MATERIAL_NAV  = {
  GRAMMAR:       (id) => `/study/grammar/sub-topic/${id}`,
  READING:       (id) => `/study/reading/${id}`,
  GD:            (id) => `/gd/${id}`,
  CONTENT_BLOCK: (id) => `/study/content-block/${id}`,
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
};

const fmtDateOnly = (str) => {
  if (!str) return "TBD";
  return new Date(str + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

/* ── Lesson assessment panel (inline in schedule) ── */
const LessonAssessPanel = ({ enrollmentId, lesson }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!lesson.curriculumId) { setLoading(false); return; }
    fetchLessonAssessmentClient(enrollmentId, lesson.curriculumId)
      .then((res) => {
        const arr = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
        setData({
          initial: arr.find((a) => a.assessmentType === "INITIAL") || null,
          final:   arr.find((a) => a.assessmentType === "FINAL")   || null,
        });
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [enrollmentId, lesson.curriculumId]);

  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "";

  if (loading) return <div style={{ padding: "0.75rem 0", color: "#9ca3af", fontSize: "0.82rem" }}>Loading assessment…</div>;
  if (!data?.initial && !data?.final) return (
    <div style={{ padding: "0.75rem 0", color: "#9ca3af", fontSize: "0.82rem" }}>No assessment recorded for this lesson yet.</div>
  );

  const ScoreRow = ({ label, score }) => {
    const color = score >= 5 ? "#16a34a" : score >= 4 ? "#65a30d" : score >= 3 ? "#d97706" : score >= 2 ? "#ea580c" : "#dc2626";
    return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
      <span style={{ fontSize: "0.78rem", color: "#64748b", width: 80, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, height: 5, background: "#e5e7eb", borderRadius: 3 }}>
        <div style={{ width: `${(score / 5) * 100}%`, height: "100%", background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: "0.78rem", fontWeight: 700, color, width: 28, textAlign: "right" }}>{score}/5</span>
    </div>
    );
  };

  const MiniCard = ({ label, a, badgeColor }) => (
    <div style={{ flex: 1, minWidth: 200, background: "#f8fafc", borderRadius: 8, padding: "0.75rem 1rem", border: "1px solid #e2e8f0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
        <span style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: badgeColor }}>{label}</span>
        <span style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>{a.totalScore}<span style={{ fontSize: "0.7rem", color: "#9ca3af", fontWeight: 400 }}>/25</span></span>
      </div>
      {Object.entries(CRITERIA_LABELS).map(([k, lbl]) => <ScoreRow key={k} label={lbl} score={a[k] || 0} />)}
      {a.improvementGoals && (
        <div style={{ margin: "10px 0 0", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 6, padding: "6px 10px" }}>
          <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>🎯 Goals</div>
          <div style={{ fontSize: "0.82rem", color: "#78350f", lineHeight: 1.4 }}>{a.improvementGoals}</div>
        </div>
      )}
      {a.instructorFeedback && (
        <div style={{ margin: "8px 0 0", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, padding: "6px 10px" }}>
          <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#1e40af", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>💬 Feedback</div>
          <div style={{ fontSize: "0.82rem", color: "#1e3a8a", fontStyle: "italic", lineHeight: 1.4 }}>"{a.instructorFeedback}"</div>
        </div>
      )}
      <div style={{ marginTop: 6, fontSize: "0.72rem", color: "#9ca3af" }}>
        {a.assessedByName ? `By ${a.assessedByName} · ${fmtDate(a.assessedAt)}` : fmtDate(a.assessedAt)}
      </div>
    </div>
  );

  return (
    <div style={{ marginTop: "0.75rem", paddingTop: "0.75rem", borderTop: "1px dashed #e2e8f0" }}>
      <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.6rem" }}>
        Lesson Assessment
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {data.initial && <MiniCard label="Initial" a={data.initial} badgeColor="#2563eb" />}
        {data.final   && <MiniCard label="Final"   a={data.final}   badgeColor="#16a34a" />}
      </div>
    </div>
  );
};

/* ── Sessions tab (merged: Schedule + Attendance) ── */
const SessionsTab = ({ meetings, enrollmentId, navigate, backUrl }) => {
  const [expandedIdx, setExpandedIdx] = useState(null);

  const attendance = meetings.filter((m) => m.attendanceStatus != null);
  const attended   = attendance.filter((a) => a.attendanceStatus === "ATTENDED").length;
  const total      = attendance.length;

  const getDateBlock = (scheduledAt, dayNumber, idx) => {
    if (scheduledAt) {
      const d = new Date(scheduledAt);
      return {
        dow:  d.toLocaleDateString("en-GB", { weekday: "short" }),
        day:  d.getDate(),
        mon:  d.toLocaleDateString("en-GB", { month: "short" }),
      };
    }
    return { dow: "Day", day: dayNumber ?? idx + 1, mon: "" };
  };

  return (
    <div>
      {/* ── Attendance summary strip ── */}
      {total > 0 && (
        <div style={{
          background: "#f8fafc", borderRadius: 10, padding: "10px 16px",
          marginBottom: 16, display: "flex", gap: 20, flexWrap: "wrap",
          fontSize: "0.85rem", color: "#374151", border: "1px solid #e5e7eb",
        }}>
          <span>✅ Attended: <strong>{attended}</strong></span>
          <span>❌ Absent: <strong>{total - attended}</strong></span>
          <span>📊 Rate: <strong>{total > 0 ? Math.round((attended / total) * 100) : 0}%</strong></span>
        </div>
      )}

      {/* ── Session rows ── */}
      {!meetings?.length ? (
        <div className="cc-empty"><p>No sessions scheduled yet.</p></div>
      ) : (
        <div className="cc-schedule-wrap">
          {meetings.map((m, idx) => {
            const statusCls =
              m.attendanceStatus === "ATTENDED"  ? "attended"   :
              m.attendanceStatus === "ABSENT"    ? "absent"     :
              m.attendanceStatus === "SCHEDULED" ? "scheduled"  : "unscheduled";
            const statusLabel =
              m.attendanceStatus === "ATTENDED"  ? "✓ Attended" :
              m.attendanceStatus === "ABSENT"    ? "✗ Absent"   :
              m.attendanceStatus === "SCHEDULED" ? "Scheduled"  : null;
            const isExpanded = expandedIdx === idx;
            const { dow, day, mon } = getDateBlock(m.scheduledAt, m.dayNumber, idx);

            return (
              <div key={idx} className={`cc-session-row cc-session-${statusCls}`}
                style={{ flexDirection: "column", alignItems: "stretch" }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                  {/* Date block */}
                  <div className="cc-session-day" style={{ minWidth: 52, height: 60, gap: 0, flexShrink: 0 }}>
                    <span className="cc-session-day-label">{dow}</span>
                    <span className="cc-session-day-num" style={{ fontSize: "1.25rem" }}>{day}</span>
                    {mon && <span style={{ fontSize: "0.6rem", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em" }}>{mon}</span>}
                  </div>

                  {/* Info + actions wrapper — stacks on mobile */}
                  <div style={{ flex: 1, minWidth: 0, display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 8 }}>
                    <div className="cc-session-info" style={{ flex: "1 1 160px", minWidth: 0 }}>
                      <div className="cc-session-title">
                        {m.title || "—"}
                        {m.isAssessment && <span className="cc-session-assess-badge">★ Assessment</span>}
                      </div>
                      {m.learningObjective && (
                        <div style={{ fontSize: "0.8rem", color: "#64748b", marginTop: 2, lineHeight: 1.4 }}>
                          {m.learningObjective}
                        </div>
                      )}
                      <div className="cc-session-meta">
                        {m.scheduledAt && (
                          <span style={{
                            fontSize: "0.85rem", fontWeight: 700,
                            background: "#dbeafe", color: "#1d4ed8",
                            border: "1px solid #bfdbfe",
                            borderRadius: 999, padding: "3px 12px",
                            display: "inline-flex", alignItems: "center", gap: 5,
                          }}>
                            🕐 {new Date(m.scheduledAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: true }).toUpperCase()}
                          </span>
                        )}
                        {statusLabel && (
                          <span className={`cc-session-status-badge cc-session-status-${statusCls}`}>{statusLabel}</span>
                        )}
                      </div>
                    </div>

                    <div className="cc-session-right" style={{ flexShrink: 0 }}>
                      {m.isJoinable && m.zoomJoinUrl ? (
                        <a href={m.zoomJoinUrl} target="_blank" rel="noreferrer"
                          className="cc-btn cc-btn-primary cc-session-join-btn"
                          onClick={(e) => e.stopPropagation()}>
                          🎥 Join Now
                        </a>
                      ) : m.zoomJoinUrl && !m.isJoinable ? (
                        <span className="cc-session-soon">⏳ Opens soon</span>
                      ) : !m.scheduledAt ? (
                        <span className="cc-session-unscheduled-label">Not scheduled</span>
                      ) : null}
                      {m.studyMaterials?.length > 0 && (
                        <button
                          className="cc-session-action-btn materials"
                          onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                        >
                          📚 {m.studyMaterials.length} Material{m.studyMaterials.length > 1 ? "s" : ""}
                        </button>
                      )}
                      <button
                        className="cc-session-action-btn feedback"
                        onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                      >
                        {isExpanded ? "Feedback ▲" : "Feedback ▼"}
                      </button>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="cc-session-expand">
                    {m.studyMaterials?.length > 0 && (
                      <div style={{ marginBottom: "1rem" }}>
                        <div className="cc-session-expand-section-label" style={{ color: "#6d28d9" }}>📚 Study Materials</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                          {m.studyMaterials.map((mat) => (
                            <button
                              key={mat.id}
                              className="cc-session-material-card"
                              onClick={() => navigate(MATERIAL_NAV[mat.contentType]?.(mat.contentId) || "#", { state: { backUrl } })}
                              style={{
                                background: mat.contentType === "GRAMMAR" ? "#ede9fe" : mat.contentType === "GD" ? "#dcfce7" : mat.contentType === "CONTENT_BLOCK" ? "#fef9c3" : "#dbeafe",
                                color:      mat.contentType === "GRAMMAR" ? "#6d28d9" : mat.contentType === "GD" ? "#15803d" : mat.contentType === "CONTENT_BLOCK" ? "#a16207" : "#1d4ed8",
                                borderColor: mat.contentType === "GRAMMAR" ? "#c4b5fd" : mat.contentType === "GD" ? "#86efac" : mat.contentType === "CONTENT_BLOCK" ? "#fde68a" : "#bfdbfe",
                              }}
                            >
                              <span>{MATERIAL_ICON[mat.contentType]} {mat.contentTitle}</span>
                              {mat.label && (
                                <span style={{ fontSize: "0.7rem", fontWeight: 400, opacity: 0.75, marginTop: 2 }}>{mat.label}</span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <LessonAssessPanel enrollmentId={enrollmentId} lesson={m} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
};

/* ── Assessment tab ── */
const AssessmentTab = ({ enrollmentId }) => {
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    fetchEnrollmentAssessments(enrollmentId)
      .then((res) => { if (res.data) setSummary(res.data); })
      .finally(() => setLoading(false));
  }, [enrollmentId]);


  if (loading) return <div className="cc-loading"><div className="cc-spinner" /></div>;

  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "";

  const ScoreBar = ({ label, score }) => {
    const color = score >= 5 ? "#16a34a" : score >= 4 ? "#65a30d" : score >= 3 ? "#d97706" : score >= 2 ? "#ea580c" : "#dc2626";
    return (
    <div className="cc-assess-row">
      <span className="cc-assess-label">{label}</span>
      <div className="cc-assess-bar-wrap">
        <div className="cc-assess-bar-fill" style={{ width: `${(score / 5) * 100}%`, background: color }} />
      </div>
      <span className="cc-assess-score" style={{ color }}>{score}/5</span>
    </div>
    );
  };

  const AssessCard = ({ label, a }) => {
    if (!a) return (
      <div className="cc-assess-card" style={{ flex: 1 }}>
        <h3>{label}</h3>
        <div className="cc-empty" style={{ padding: "1rem 0" }}><p style={{ margin: 0 }}>Not yet completed.</p></div>
      </div>
    );
    return (
      <div className="cc-assess-card" style={{ flex: 1 }}>
        <h3>{label}</h3>
        {Object.entries(CRITERIA_LABELS).map(([k, lbl]) => (
          <ScoreBar key={k} label={lbl} score={a[k] || 0} />
        ))}
        <div className="cc-assess-total"><span>Total</span><span>{a.totalScore}/25</span></div>
        {a.instructorFeedback && (
          <p style={{ marginTop: 10, fontSize: "0.85rem", color: "#64748b", fontStyle: "italic" }}>"{a.instructorFeedback}"</p>
        )}
        {a.improvementGoals && (
          <p style={{ marginTop: 6, fontSize: "0.82rem", color: "#64748b" }}>Goals: {a.improvementGoals}</p>
        )}
        <div style={{ marginTop: 8, fontSize: "0.78rem", color: "#9ca3af" }}>
          {a.assessedByName ? `By ${a.assessedByName} · ${fmtDate(a.assessedAt)}` : fmtDate(a.assessedAt)}
        </div>
      </div>
    );
  };

  // course-level from summary API
  const cInitial  = summary?.initial;
  const cFinal    = summary?.finalAssessment;
  const delta     = summary?.scoreDelta;
  const improved  = summary?.improved;

  return (
    <div>
      {/* ── Course Progress ── */}
      <div>
          <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#028891", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.75rem" }}>
            Your Course Assessment
          </div>
          {delta != null && (
            <div className={`cc-assess-delta ${improved ? "positive" : "negative"}`} style={{ marginBottom: 16, display: "inline-block" }}>
              {improved ? `\ud83c\udf89 You improved by ${delta} points!` : `\u25bc ${Math.abs(delta)} points change`}
            </div>
          )}
          {!cInitial && <div style={{ color: "#9ca3af", fontSize: "0.88rem", marginBottom: 12 }}>Initial assessment not done yet.</div>}
          {cInitial && !cFinal && <div style={{ color: "#9ca3af", fontSize: "0.88rem", marginBottom: 12 }}>Final assessment pending.</div>}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
            <AssessCard label="Initial Assessment" a={cInitial} />
            <AssessCard label="Final Assessment"   a={cFinal} />
          </div>
      </div>
    </div>
  );
};


const DOW_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const fmtCalMonth = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const buildCalGrid = (year, month) => {
  const first = new Date(year, month, 1).getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const cells = Array(first).fill(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

/* ── Unavailability Modal ── */
const UnavailModal = ({ date, batchId, onClose, onSuccess }) => {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      await submitUnavailability({ batchId, date, reason });
      onSuccess();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Submission failed.";
      setError(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cal-modal-overlay" onClick={onClose}>
      <div className="cal-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Request Unavailability</h3>
        <p style={{ margin: "0 0 0.75rem", fontSize: "0.88rem", color: "#374151" }}>
          📅 <strong>{new Date(date + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</strong>
        </p>
        <textarea
          placeholder="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {error && (
          <div style={{ marginTop: "0.5rem", fontSize: "0.82rem", color: "#dc2626", background: "#fee2e2", padding: "0.5rem 0.75rem", borderRadius: 6 }}>
            {error}
          </div>
        )}
        <div className="cal-modal-actions">
          <button onClick={onClose} style={{ padding: "0.45rem 1rem", border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", cursor: "pointer", fontSize: "0.85rem" }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={submitting} style={{ padding: "0.45rem 1rem", border: "none", borderRadius: 6, background: "#2563eb", color: "#fff", cursor: "pointer", fontSize: "0.85rem", fontWeight: 600 }}>
            {submitting ? "Submitting…" : "Submit Request"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Calendar tab ── */
const CalendarTab = ({ batchId, batchMode }) => {
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const [cur, setCur] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [calDays, setCalDays] = useState([]);
  const [calLoading, setCalLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [subTab, setSubTab] = useState("month");
  const [requests, setRequests] = useState([]);
  const [reqLoading, setReqLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadCal = useCallback(async () => {
    setCalLoading(true);
    try {
      const res = await getClientBatchCalendar(batchId, fmtCalMonth(cur));
      setCalDays(Array.isArray(res.data) ? res.data : []);
    } finally {
      setCalLoading(false);
    }
  }, [batchId, cur]);

  const loadReqs = useCallback(async () => {
    setReqLoading(true);
    try {
      const res = await getMyUnavailability(batchId);
      setRequests(Array.isArray(res.data) ? res.data : []);
    } finally {
      setReqLoading(false);
    }
  }, [batchId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadCal(); }, [loadCal]);
  useEffect(() => { if (subTab === "requests") loadReqs(); }, [subTab, loadReqs]);

  const dayMap = Object.fromEntries(calDays.map((d) => [d.date, d]));
  const cells = buildCalGrid(cur.getFullYear(), cur.getMonth());

  return (
    <div style={{ paddingTop: "0.75rem" }}>
      {/* sub-tabs */}
      <div style={{ display: "flex", gap: "0.25rem", marginBottom: "1rem", borderBottom: "2px solid #e5e7eb" }}>
        {["month", "requests"].map((t) => (
          <button key={t} onClick={() => setSubTab(t)} style={{
            padding: "0.4rem 0.9rem", border: "none", background: "none", cursor: "pointer",
            fontWeight: subTab === t ? 700 : 400,
            color: subTab === t ? "#2563eb" : "#6b7280",
            borderBottom: subTab === t ? "2px solid #2563eb" : "2px solid transparent",
            marginBottom: "-2px", fontSize: "0.85rem",
          }}>
            {t === "month" ? "📅 Calendar" : `✋ My Requests${requests.length ? ` (${requests.length})` : ""}`}
          </button>
        ))}
      </div>

      {subTab === "month" && (
        <>
          <div className="cal-wrap">
            <div className="cal-nav">
              <button className="cal-nav-btn" onClick={() => setCur(new Date(cur.getFullYear(), cur.getMonth() - 1, 1))}>‹</button>
              <h3>{cur.toLocaleString("default", { month: "long", year: "numeric" })}</h3>
              <button className="cal-nav-btn" onClick={() => setCur(new Date(cur.getFullYear(), cur.getMonth() + 1, 1))}>›</button>
            </div>
            <div className="cal-dow-row">
              {DOW_LABELS.map((d) => <div key={d} className="cal-dow-cell">{d}</div>)}
            </div>
            {calLoading ? (
              <div style={{ padding: "2rem", textAlign: "center", color: "#9ca3af" }}>Loading…</div>
            ) : (
              <div className="cal-grid">
                {cells.map((day, i) => {
                  if (!day) return <div key={i} className="cal-cell cal-empty" />;
                  const dateStr = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  const info = dayMap[dateStr];
                  const isStudy = info?.dayType === "STUDY_DAY";
                  const isHoliday = info?.dayType === "HOLIDAY";
                  const isFuture = dateStr > todayStr;
                  const isToday = dateStr === todayStr;
                  return (
                    <div
                      key={i}
                      className={`cal-cell ${isStudy ? "cal-study" : isHoliday ? "cal-holiday" : ""} ${isToday ? "cal-today" : ""}`}
                      onClick={() => isStudy && isFuture && setModal(dateStr)}
                      style={{ cursor: isStudy && isFuture ? "pointer" : "default" }}
                      title={isStudy && isFuture ? "Tap to request unavailability" : undefined}
                    >
                      <div className="cal-day-num">{day}</div>
                      {isStudy && <span style={{ fontSize: "0.65rem", color: "#15803d" }}>Study{isFuture ? " ✋" : ""}</span>}
                      {isHoliday && <span style={{ fontSize: "0.65rem", color: "#b91c1c", textDecoration: "line-through" }}>Holiday</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div style={{ marginTop: "0.6rem", display: "flex", gap: "1rem", fontSize: "0.78rem", color: "#6b7280", flexWrap: "wrap" }}>
            <span><span className="cal-dot cal-dot-study" /> Study Day (✋ = tap to request unavailability)</span>
            <span><span className="cal-dot cal-dot-holiday" /> Holiday</span>
          </div>
        </>
      )}

      {subTab === "requests" && (
        <div>
          {reqLoading ? (
            <div style={{ padding: "1rem", color: "#9ca3af" }}>Loading…</div>
          ) : requests.length === 0 ? (
            <div style={{ padding: "1.5rem", textAlign: "center", color: "#9ca3af" }}>No requests yet.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {requests.map((r) => (
                <div key={r.id} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "0.9rem 1rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "0.5rem" }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>📅 {new Date(r.date + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</div>
                      {r.reason && <div style={{ fontSize: "0.82rem", color: "#6b7280", marginTop: 2 }}>{r.reason}</div>}
                    </div>
                    <span className={`cal-status cal-status-${r.status}`}>{r.status}</span>
                  </div>
                  {r.status === "REJECTED" && r.adminNotes && (
                    <div style={{ marginTop: "0.5rem", fontSize: "0.82rem", color: "#b91c1c", background: "#fee2e2", padding: "0.4rem 0.6rem", borderRadius: 5 }}>
                      Reason: {r.adminNotes}
                    </div>
                  )}
                  {r.status === "APPROVED" && (
                    <div style={{ marginTop: "0.5rem", fontSize: "0.82rem", color: "#15803d", background: "#dcfce7", padding: "0.4rem 0.6rem", borderRadius: 5 }}>
                      {batchMode === "PERSONAL"
                        ? "Reschedule will be arranged by admin."
                        : "You will be marked as absent for this session."}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {modal && (
        <UnavailModal
          date={modal}
          batchId={batchId}
          onClose={() => setModal(null)}
          onSuccess={() => { setRefreshKey((k) => k + 1); loadCal(); }}
        />
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   Main page
───────────────────────────────────────────── */
const MyEnrollmentDetailPage = () => {
  const { enrollmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, authLoading } = useAuth();

  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState("sessions");

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?redirect=/my-courses", { replace: true }); return; }
    fetchEnrollmentDetail(enrollmentId)
      .then((res) => setEnrollment(res.data))
      .catch(() => setEnrollment(null))
      .finally(() => setLoading(false));
  }, [enrollmentId, authLoading, user, navigate]);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="cc-loading"><div className="cc-spinner" /><p>Loading…</p></div>
      </>
    );
  }

  if (!enrollment) {
    return (
      <>
        <Navbar />
        <div className="cc-empty">
          <div className="cc-empty-icon">❌</div>
          <p>Enrollment not found.</p>
          <button className="cc-btn cc-btn-outline" onClick={() => navigate("/my-courses")}>
            ← Back to My Courses
          </button>
        </div>
      </>
    );
  }

  // Guard: PENDING_PAYMENT enrollments have no lessons yet
  if (enrollment.status === "PENDING_PAYMENT") {
    return (
      <>
        <Navbar />
        <div className="cc-page" style={{ paddingTop: 24 }}>
          <button
            className="cc-btn cc-btn-outline"
            style={{ marginBottom: 16, padding: "6px 14px", fontSize: "0.82rem" }}
            onClick={() => navigate("/my-courses")}
          >
            ← My Courses
          </button>
          <div style={{ background: "#fffbeb", borderRadius: 12, padding: "2rem", maxWidth: 480, textAlign: "center" }}>
            <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>⏳</div>
            <h2 style={{ margin: "0 0 0.5rem", color: "#92400e" }}>Awaiting Confirmation</h2>
            <p style={{ color: "#4b5563", fontSize: "0.9rem", margin: 0 }}>
              Your enrollment for <strong>{enrollment.courseName}</strong> ({enrollment.batchName}) is pending payment confirmation.
              Admin will contact you on your registered WhatsApp/phone.
            </p>
          </div>
        </div>
      </>
    );
  }

  const lessons  = enrollment.lessons || [];
  const meetings = lessons;

  return (
    <>
      <Navbar />
      <div className="cc-page" style={{ paddingTop: 24 }}>

        {/* back */}
        <button
          className="cc-btn cc-btn-outline"
          style={{ marginBottom: 16, padding: "6px 14px", fontSize: "0.82rem" }}
          onClick={() => navigate("/my-courses")}
        >
          ← My Courses
        </button>

        {/* header card */}
        <div className="cc-enroll-header">
          <div className="cc-enroll-header-info">
            <div className="cc-enroll-header-chips">
              <span className={`cc-enroll-status cc-enroll-${enrollment.status}`}>
                {enrollment.status === "ACTIVE" ? "Active" : enrollment.enrollmentStatus || enrollment.status}
              </span>
              <span className={`cc-mode cc-mode-${enrollment.mode}`}>
                {enrollment.mode === "BATCH" ? "👥 Batch" : "👤 Personal Coaching"}
              </span>
            </div>
            <h2 className="cc-enroll-course-name">{enrollment.courseName}</h2>
            <div className="cc-enroll-header-meta">
              <span>👨🏫 {enrollment.instructorName || "TBD"}</span>
              <span>📅 Starts {fmtDateOnly(enrollment.batchStartDate)}</span>
              {enrollment.attendedCount != null && enrollment.totalLessons != null && (
                <span>✅ {enrollment.attendedCount}/{enrollment.totalLessons} sessions completed</span>
              )}
            </div>
          </div>
        </div>

        {/* tabs */}
        <div className="cc-tabs">
          {["sessions", "calendar", "assessment"].map((tab) => (
            <button
              key={tab}
              className={`cc-tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === "sessions" ? "Sessions" : tab === "calendar" ? "Calendar" : "Assessment"}
              {tab === "sessions" && meetings.length > 0 && (
                <span style={{
                  marginLeft: 5, background: "#e0f2fe", color: "#0369a1",
                  borderRadius: 999, padding: "0 6px", fontSize: "0.72rem",
                }}>
                  {meetings.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === "sessions"   && <SessionsTab meetings={meetings} enrollmentId={enrollmentId} navigate={navigate} backUrl={location.pathname} />}
        {activeTab === "calendar"   && <CalendarTab batchId={enrollment.batchId} batchMode={enrollment.mode} />}
        {activeTab === "assessment" && <AssessmentTab enrollmentId={enrollmentId} />}
      </div>
    </>
  );
};

export default MyEnrollmentDetailPage;
